/**
 * The tiles entered so far in a round form, kept on the phone as they are entered (Indra, 2026-10-04), so a page reload in the middle of a
 * round — the player's own refresh, or a phone that discarded a background tab — does not lose the hand being built. It is cleared when the
 * round is recorded or submitted. Storage can be unavailable (a private window), so every access is guarded: a failure only means the hand
 * is not remembered.
 */

import type { TileGridPrefill, TileGridResult } from './tileGrid';

const PREFIX = 'mahjong-scorer:draftHand:';

export function saveDraftHand(key: string, hand: TileGridResult): void {
  try {
    const empty = hand.concealed.length === 0 && hand.melds.length === 0 && hand.flowers.length === 0 && !hand.claimedSet;
    if (empty) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, JSON.stringify(hand));
  } catch {
    // not remembered; nothing else is affected
  }
}

export function loadDraftHand(key: string): TileGridPrefill | undefined {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    const d = JSON.parse(raw) as Partial<TileGridResult> | null;
    if (!d || !Array.isArray(d.concealed) || !Array.isArray(d.melds)) return undefined;
    return {
      concealed: d.concealed.filter((t): t is string => typeof t === 'string'),
      melds: d.melds as TileGridPrefill['melds'],
      ...(typeof d.winningTile === 'string' ? { winningTile: d.winningTile } : {}),
      flowers: Array.isArray(d.flowers) ? d.flowers.filter((t): t is string => typeof t === 'string') : [],
      claimedSet: d.claimedSet === true,
    };
  } catch {
    return undefined;
  }
}

export function clearDraftHand(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    // nothing to do
  }
}
